import React, { useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { FormError } from '../ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Image as ImageIcon, Check, AlertCircle } from 'lucide-react'
import { promotionSectionService } from '../../services/promotion-section.service'
import { promotionService } from '../../services/promotion.service'
import { SortablePromotionItem } from './SortablePromotionItem'
import { useQuery, useQueryClient } from '@tanstack/react-query'

const promotionItemSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1, 'El nombre es requerido'),
  image_url: z.string().optional(),
  call_to_action_url: z.string().url('URL no válida').optional().or(z.literal('')),
  promotion_id: z.number().optional().nullable(),
  sort_order: z.number(),
  is_active: z.boolean(),
  file: z.any().optional(),
  preview_url: z.string().optional(),
}).refine(data => data.image_url || data.file, {
  message: "La imagen es requerida",
  path: ["image_url"]
})

const promotionSectionSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  title: z.string().optional().nullable(),
  subtitle: z.string().optional().nullable(),
  is_active: z.boolean(),
  starts_at: z.string().optional().nullable(),
  ends_at: z.string().optional().nullable(),
  items: z.array(promotionItemSchema).length(4, 'Deben existir exactamente 4 cards')
}).superRefine((data, ctx) => {
  if (data.starts_at && data.ends_at) {
    if (new Date(data.starts_at) > new Date(data.ends_at)) {
      ctx.addIssue({
        code: 'custom',
        message: 'La fecha de inicio no puede ser posterior a la de fin',
        path: ['starts_at'],
      })
    }
  }
})

type PromotionSectionFormValues = z.infer<typeof promotionSectionSchema>

interface PromotionSectionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sectionId: number | null
  onSuccess: () => void
}

export function PromotionSectionModal({ open, onOpenChange, sectionId, onSuccess }: PromotionSectionModalProps) {
  const isEditing = !!sectionId
  const queryClient = useQueryClient()
  
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PromotionSectionFormValues>({
    resolver: zodResolver(promotionSectionSchema),
    defaultValues: {
      name: '',
      title: '',
      subtitle: '',
      is_active: true,
      starts_at: '',
      ends_at: '',
      items: Array.from({ length: 4 }).map((_, i) => ({
        name: '',
        sort_order: i + 1,
        is_active: true,
      }))
    }
  })

  const { fields: items, move } = useFieldArray({
    control,
    name: 'items',
  })

  const isActiveValue = watch('is_active')

  const { data: section, isLoading } = useQuery({
    queryKey: ['promotionSection', sectionId],
    queryFn: () => promotionSectionService.getSection(sectionId!),
    enabled: !!sectionId && open,
  })

  const { data: promotionsResponse } = useQuery({
    queryKey: ['activePromotionsForCards'],
    queryFn: () => promotionService.list({ channel: 'ecommerce', lifecycle_status: 'active', per_page: 100 }),
    enabled: open,
  })
  const activePromotions = promotionsResponse?.data || []

  useEffect(() => {
    if (section && open) {
      reset({
        name: section.name,
        title: section.title || '',
        subtitle: section.subtitle || '',
        is_active: section.is_active,
        starts_at: section.starts_at ? section.starts_at.split('T')[0] : '',
        ends_at: section.ends_at ? section.ends_at.split('T')[0] : '',
        items: section.items.map(item => ({
          ...item,
          call_to_action_url: item.call_to_action_url || '',
          preview_url: item.image_url
        }))
      })
    } else if (open && !isEditing) {
       reset({
        name: '',
        title: '',
        subtitle: '',
        is_active: true,
        starts_at: '',
        ends_at: '',
        items: Array.from({ length: 4 }).map((_, i) => ({
          name: '',
          sort_order: i + 1,
          is_active: true,
        }))
      })
    }
  }, [section, open, reset, isEditing])

  const handleFileChange = (index: number, file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setValue(`items.${index}.file`, file)
    setValue(`items.${index}.preview_url`, previewUrl)
    setValue(`items.${index}.image_url`, undefined)
  }

  const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
    move(dragIndex, hoverIndex)
  }, [move])

  const onSubmit = async (data: PromotionSectionFormValues) => {
    try {
      const formData = new FormData()
      formData.append('name', data.name)
      if (data.title) formData.append('title', data.title)
      if (data.subtitle) formData.append('subtitle', data.subtitle)
      formData.append('is_active', data.is_active ? '1' : '0')
      if (data.starts_at) formData.append('starts_at', data.starts_at)
      if (data.ends_at) formData.append('ends_at', data.ends_at)

      data.items.forEach((item, index) => {
        const order = index + 1
        formData.append(`items[${index}][sort_order]`, String(order))
        formData.append(`items[${index}][is_active]`, item.is_active ? '1' : '0')
        formData.append(`items[${index}][name]`, item.name)

        if (item.id) formData.append(`items[${index}][id]`, String(item.id))
        formData.append(`items[${index}][call_to_action_url]`, item.call_to_action_url || '')
        formData.append(`items[${index}][promotion_id]`, item.promotion_id ? String(item.promotion_id) : '')

        if (item.file) {
          formData.append(`items[${index}][file]`, item.file)
        } else if (item.image_url) {
          formData.append(`items[${index}][image_url]`, item.image_url)
        }
      })

      if (isEditing && sectionId) {
        await promotionSectionService.updateSection(sectionId, formData)
        queryClient.invalidateQueries({ queryKey: ['promotionSection', sectionId] })
        toast.success('Sección actualizada exitosamente')
      } else {
        await promotionSectionService.createSection(formData)
        toast.success('Sección creada exitosamente')
      }
      onSuccess()
    } catch (error) {
      toast.error('Ocurrió un error al guardar la sección')
      console.error(error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
              <ImageIcon className="h-5 w-5 text-primary" />
              {isEditing ? 'Editar Sección de Promociones' : 'Nueva Sección de Promociones'}
            </DialogTitle>
            <DialogDescription className="text-card-description">
              {isEditing
                ? 'Modifica los datos de la sección y sus cards.'
                : 'Configura un bloque de 4 promociones para la tienda en línea.'}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Información General */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Nombre Interno <span className="text-red-500">*</span>
                  </label>
                  <Input placeholder="Ej. Bloque Promociones Verano" {...register('name')} />
                  <FormError message={errors.name?.message} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Título a mostrar (Opcional)
                  </label>
                  <Input placeholder="Ej. Nuestras Ofertas" {...register('title')} />
                  <FormError message={errors.title?.message} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Subtítulo a mostrar (Opcional)
                  </label>
                  <Input placeholder="Ej. Lo mejor para tu salud" {...register('subtitle')} />
                  <FormError message={errors.subtitle?.message} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Fecha de Inicio (Opcional)
                  </label>
                  <Input type="date" {...register('starts_at')} />
                  <FormError message={errors.starts_at?.message} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Fecha de Fin (Opcional)
                  </label>
                  <Input type="date" {...register('ends_at')} />
                  <FormError message={errors.ends_at?.message} />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={isActiveValue}
                  onClick={() => setValue('is_active', !isActiveValue)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-primary' : 'bg-slate-200'
                    }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                      }`}
                  />
                </button>
                <span className="text-sm font-medium text-card-foreground">Sección Activa</span>
              </div>

              <hr />

              {/* SLIDES */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Cards de Promoción (Siempre 4)</h3>
                    <p className="text-sm text-slate-500">Arrastra para reordenar las cards.</p>
                  </div>
                </div>

                {errors.items?.root && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-md">
                    <AlertCircle size={16} />
                    {errors.items.root.message}
                  </div>
                )}

                <DndProvider backend={HTML5Backend}>
                  <div className="grid grid-cols-1 gap-3">
                    {items.map((field, index) => (
                      <SortablePromotionItem
                        key={field.id}
                        index={index}
                        item={watch(`items.${index}`)}
                        moveItem={moveItem}
                        onFileChange={(file) => handleFileChange(index, file)}
                        register={register}
                        errors={errors}
                        title={`Posición ${index + 1}`}
                        activePromotions={activePromotions}
                        setValue={setValue}
                      />
                    ))}
                  </div>
                </DndProvider>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-end mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
              {isSubmitting ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
